import React from 'react';
import PropTypes from 'prop-types';
import round from 'lodash/round';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';

import { Loader } from 'ui';
import { EmptyDataLabel } from 'ui/Labels';
import { parseToCurrency } from 'shared/functions';
import { useReportPage } from 'shared/hooks';
import WeightIcon from 'assets/icons/weight.svg';

const useStyle = makeStyles((theme) => ({
  title: {
    display: 'flex',
    height: useReportPage ? 16 : 35,
    alignItems: 'center',
    fontSize: useReportPage ? 10 : 14,
  },
  item: {
    maxWidth: 250,

    '&:first-child': {
      marginRight: 15,
    },
  },
  itemContainer: {
    justifyContent: 'space-between',
  },
  calculatorTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
    fontSize: useReportPage ? 12 : 16,
  },
  triangle: {
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderWidth: '28px 85px 0 85px',
    borderColor: `${grey[400]} transparent transparent transparent`,
    margin: useReportPage ? '-5px auto' : '10px auto',
    transform: useReportPage ? 'scale(0.7)' : 'none',

    '&:after': {
      content: '""',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: '25px 75px 0 75px',
      borderColor: `${theme.palette.custom.white} transparent transparent transparent`,
      transform: 'translate(-50%, -106%)',
    },
  },
  weightBox: {
    display: 'flex',
    justifyContent: 'center',
    width: 100,
    borderWidth: 1,
    borderRadius: 20,
    borderColor: grey[400],
    borderStyle: 'solid',
    padding: 10,
    margin: useReportPage ? '-5px auto 0' : '15px auto 0',
    transform: useReportPage ? 'scale(0.7)' : 'none',
  },
  weight: {
    fontSize: 18,
    marginLeft: 10,
  },
  textField: {
    margin: theme.spacing(1, 0),
    background: theme.palette.custom.lightGray,
    textAlign: 'center',
    borderRadius: theme.spacing(1),
  },
  textFieldValue: {
    fontWeight: 'bold',
    padding: theme.spacing(0.5, 0),
    fontSize: useReportPage ? 12 : 16,
  },
}));

export const ESGDistributionCalculator = ({
  title,
  weight,
  firstInputLabel,
  firstInputValue,
  secondInputLabel,
  secondInputValue,
  firstInputUnit,
  secondInputUnit,
  showWeightBox,
}) => {
  const classes = useStyle();
  const firstValue = firstInputValue ? round(firstInputValue, 4) : '';
  const secondValue = secondInputValue || '';

  return (
    <>
      <Typography component="h6" variant="h6" className={classes.calculatorTitle}>
        {title}
      </Typography>
      <Grid container className={classes.itemContainer}>
        <Grid item xs className={classes.item}>
          <Typography variant="body2" color="primary" className={classes.title}>
            {firstInputLabel}
          </Typography>
          <div className={classes.textField}>
            <Typography component="h6" variant="h6" className={classes.textFieldValue}>
              {firstValue} {firstInputUnit}
            </Typography>
          </div>
        </Grid>
        <Grid item xs className={classes.item}>
          <Typography variant="body2" color="primary" className={classes.title}>
            {secondInputLabel}
          </Typography>
          <div className={classes.textField}>
            <Typography component="h6" variant="h6" className={classes.textFieldValue}>
              {secondValue} {secondInputUnit}
            </Typography>
          </div>
        </Grid>
      </Grid>
      {showWeightBox && (
        <>
          <Box className={classes.triangle} />
          <Box className={classes.weightBox}>
            <WeightIcon /> <Typography className={classes.weight}>{weight}%</Typography>
          </Box>
        </>
      )}
    </>
  );
};

ESGDistributionCalculator.defaultProps = {
  firstInputValue: null,
  secondInputValue: null,
  showWeightBox: true,
  weight: 0,
  title: '',
};

ESGDistributionCalculator.propTypes = {
  title: PropTypes.string,
  weight: PropTypes.number,
  firstInputLabel: PropTypes.string.isRequired,
  secondInputLabel: PropTypes.string.isRequired,
  firstInputUnit: PropTypes.string.isRequired,
  secondInputUnit: PropTypes.string.isRequired,
  firstInputValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  secondInputValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showWeightBox: PropTypes.bool,
};

const useESGDistributionStyles = makeStyles({
  root: {
    position: 'relative',
  },
  divider: {
    alignSelf: 'stretch',
    height: useReportPage ? 80 : 110,
  },
  legend: {
    fontSize: useReportPage ? 10 : 14,
    fontWeight: 500,
  },
});

const ESGDistribution = ({ data, loading }) => {
  const { t } = useTranslation();
  const classes = useESGDistributionStyles();

  return (
    <Paper className={classes.root}>
      <Box p={2} pt={useReportPage ? 1 : 2}>
        <Typography variant="h6">{t('Monetisation distribution')}</Typography>
        {loading ? (
          <Loader center />
        ) : (
          <>
            {!data ? (
              <Box py={2}>
                <EmptyDataLabel label={t('There are no data available yet.')} />
              </Box>
            ) : (
              <>
                <Box py={2} pt={useReportPage ? 1 : 2}>
                  <Divider />
                </Box>
                <Grid container>
                  <Grid item xs>
                    <Box pb={1} px={3}>
                      <ESGDistributionCalculator
                        title={t('Environment')}
                        weight={+data.environmentWeight.toFixed(1)}
                        firstInputLabel={t('Total net externality per EUR revenue')}
                        firstInputValue={data.environmentExtCostsDividedByRevenue.toFixed(2)}
                        secondInputLabel={t('Absolute net externality')}
                        secondInputValue={parseToCurrency(data.environmentExtCosts)}
                        firstInputUnit="EUR"
                        secondInputUnit="mEUR"
                      />
                    </Box>
                  </Grid>
                  <Divider className={classes.divider} orientation="vertical" />
                  <Grid item xs>
                    <Box pb={1} px={3}>
                      <ESGDistributionCalculator
                        title={t('Social')}
                        weight={+data.socialWeight.toFixed(1)}
                        firstInputLabel={t('Total net externality per EUR revenue')}
                        firstInputValue={data.socialRiskHoursExtCostsDividedByRevenue.toFixed(2)}
                        secondInputLabel={t('Absolute net externality')}
                        secondInputValue={parseToCurrency(data.socialRiskHoursExtCosts)}
                        firstInputUnit="EUR"
                        secondInputUnit="mEUR"
                      />
                    </Box>
                  </Grid>
                  <Divider className={classes.divider} orientation="vertical" />
                  <Grid item xs>
                    <Box pb={1} px={3}>
                      <ESGDistributionCalculator
                        title={t('Governance')}
                        weight={+data.governanceWeight.toFixed(1)}
                        firstInputLabel={t('Total net externality per EUR revenue')}
                        firstInputValue={data.corruptionRiskDividedByRevenue.toFixed(2)}
                        secondInputLabel={t('Absolute net externality')}
                        secondInputValue={parseToCurrency(data.corruptionRisk)}
                        firstInputUnit="EUR"
                        secondInputUnit="mEUR"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

ESGDistribution.defaultProps = {
  data: null,
};

ESGDistribution.propTypes = {
  data: PropTypes.shape({
    governanceWeight: PropTypes.number.isRequired,
    corruptionRiskDividedByRevenue: PropTypes.number.isRequired,
    corruptionRisk: PropTypes.number.isRequired,
    environmentWeight: PropTypes.number.isRequired,
    environmentExtCostsDividedByRevenue: PropTypes.number.isRequired,
    environmentExtCosts: PropTypes.number.isRequired,
    socialWeight: PropTypes.number.isRequired,
    socialRiskHoursExtCostsDividedByRevenue: PropTypes.number.isRequired,
    socialRiskHoursExtCosts: PropTypes.number.isRequired,
  }),
  loading: PropTypes.bool.isRequired,
};

export default ESGDistribution;
